import React, { memo, useCallback, useEffect, useState } from "react";

import { formatCurrency } from "helpers";
import { estimateTenantSubscriptionAPI } from "services/api/subscriptions/api";
import { SubscriptionEstimate } from "services/api/subscriptions/types";
import { Loader } from "ui/components/Loader";

import { TenantSubscriptionSummaryProps } from "./types";

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString("hr-HR");

const BILLING_PERIOD_LABELS: Record<string, string> = {
  Monthly: "Mjesečno",
  Annual: "Godišnje",
};

const STATUS_LABELS: Record<string, string> = {
  Trial: "Probni period",
  Active: "Aktivna",
  Cancelled: "Otkazana",
  PendingApproval: "Čeka odobrenje",
};

/**
 * Sažetak pretplate prije potpisa.
 *
 * Iznos se ne prepisuje iz koraka nego se ponovno traži od backenda — sažetak i potpis moraju
 * gledati isti izračun, a ne dvije kopije brojke.
 */
const TenantSubscriptionSummary: React.FC<TenantSubscriptionSummaryProps> = (props) => {
  const { tenantId, isPerson, subscriptionValues, existingSubscription } = props;

  const [estimate, setEstimate] = useState<SubscriptionEstimate | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchEstimate = useCallback(async () => {
    if (!tenantId || isPerson || existingSubscription) {
      setEstimate(null);
      return;
    }

    setIsLoading(true);

    try {
      setEstimate(await estimateTenantSubscriptionAPI(tenantId, subscriptionValues));
    } catch {
      // poruka ide kroz snackbar; radije prazno nego pogrešan iznos
      setEstimate(null);
    }

    setIsLoading(false);
  }, [tenantId, isPerson, existingSubscription, subscriptionValues]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  /*---------------------------------------------------------------
      OSOBA — nema pretplate
    ---------------------------------------------------------------*/
  if (isPerson) {
    return (
      <div className="grid grid-cols-2 gap-1 text-sm">
        <div className="col-span-2">
          <span className="text-slate-400">Pretplata:</span>
          <span className="ml-2 text-slate-700 font-medium">
            Nema — fizička osoba koristi besplatno.
          </span>
        </div>
      </div>
    );
  }

  /*---------------------------------------------------------------
      POSTOJEĆA PRETPLATA — ne potpisuje se nova
    ---------------------------------------------------------------*/
  if (existingSubscription) {
    return (
      <div className="grid grid-cols-2 gap-1 text-sm">
        <div>
          <span className="text-slate-400">Paketi:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {existingSubscription.packCodes.join(", ") || "-"}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Stanje:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {STATUS_LABELS[existingSubscription.status] ?? existingSubscription.status}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Plaćanje:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {BILLING_PERIOD_LABELS[existingSubscription.billingPeriod] ??
              existingSubscription.billingPeriod}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Počela:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {formatDate(existingSubscription.startsOn)}
          </span>
        </div>
        <p className="col-span-2 text-xs text-slate-500 mt-2">
          Klijent već ima pretplatu, pa se nova neće potpisati. Mijenja se na ekranu klijenta.
        </p>
      </div>
    );
  }

  /*---------------------------------------------------------------
      NOVA PRETPLATA
    ---------------------------------------------------------------*/
  return (
    <div>
      <div className="grid grid-cols-2 gap-1 text-sm">
        <div>
          <span className="text-slate-400">Paketi:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {subscriptionValues.packCodes.join(", ") || "-"}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Plaćanje:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {BILLING_PERIOD_LABELS[subscriptionValues.billingPeriod]}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Početak:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {formatDate(subscriptionValues.startsOn)}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Probni period:</span>
          <span className="ml-2 text-slate-700 font-medium">
            {subscriptionValues.withTrial
              ? estimate?.trialEndsOn
                ? `do ${formatDate(estimate.trialEndsOn)}`
                : "da"
              : "ne"}
          </span>
        </div>
      </div>

      {isLoading && (
        <div className="mt-3">
          <Loader isLoading={true}> </Loader>
        </div>
      )}

      {!isLoading && estimate?.amounts && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-400">Pretplata mjesečno</span>
            <span className="text-slate-700 font-medium">
              {formatCurrency.format(estimate.amounts.fullMonth)}
            </span>
          </div>

          {estimate.amounts.annualTotal !== null && (
            <div className="flex justify-between">
              <span className="text-slate-400">Godišnje</span>
              <span className="text-slate-700 font-medium">
                {formatCurrency.format(estimate.amounts.annualTotal)}
              </span>
            </div>
          )}

          {subscriptionValues.overrides.length > 0 && (
            <div className="flex justify-between mt-1">
              <span className="text-slate-400">Posebni uvjeti</span>
              <span className="text-slate-700 font-medium">
                {subscriptionValues.overrides.length}
              </span>
            </div>
          )}
        </div>
      )}

      {!isLoading && estimate && !estimate.amounts && (
        <p className="text-sm text-amber-700 mt-3">
          {estimate.errors[0] ?? "Iznos nije izračunat."}
        </p>
      )}
    </div>
  );
};

export default memo(TenantSubscriptionSummary);
